import mixpanel from 'mixpanel-browser'
import { getLogger } from '@/logger'
import { getMixpanelConfig } from '@/env'

export const GalleryAnalyticsEvent = {
  LINK_OPENED: 'GoTo > Gallery - Link Opened',
  LINK_OPEN_FAILED: 'GoTo > Gallery - Link Open Failed',
  VIEW_MODE_CHANGED: 'GoTo > Gallery - View Mode Changed',
  FILE_OPENED: 'GoTo > Gallery - File Opened',
  DOWNLOAD_REQUESTED: 'GoTo > Gallery - Download Requested',
  DOWNLOAD_SUCCEEDED: 'GoTo > Gallery - Download Succeeded',
  DOWNLOAD_FAILED: 'GoTo > Gallery - Download Failed',
} as const

interface AnalyticsProperties {
  [key: string]: unknown
}

export interface GalleryAnalyticsContext {
  galleryId: string
  preview: boolean
}

function getSafeProperties(eventName: string, properties: AnalyticsProperties): AnalyticsProperties {
  const safeProperties: AnalyticsProperties = {}

  for (const prop in properties) {
    const value = properties[prop]

    const type = typeof value
    const isAcceptedPrimitiveType = type === 'string' || type === 'number' || type === 'boolean'
    if (!isAcceptedPrimitiveType && !Array.isArray(value)) {
      const description = `The mixpanel event named "${eventName}" contains an unsupported value in ${prop}`
      getLogger().warn('Invalid property type', new Error(description))
    } else if (value !== undefined) {
      safeProperties[prop] = value
    }
  }

  return safeProperties
}

export interface GalleryAnalyticsService {
  initialize(context: GalleryAnalyticsContext): void
  track(eventName: string, properties: AnalyticsProperties): void
}

export function createGalleryAnalyticsService(): GalleryAnalyticsService {
  let initialized = false

  const initialize = (context: GalleryAnalyticsContext): void => {
    if (initialized) {
      return
    }

    try {
      const config = getMixpanelConfig()
      if (!config.token) {
        getLogger().warn('[gallery-analytics] mixpanel token missing, analytics disabled')
        return
      }

      mixpanel.init(config.token, {
        api_host: config.apiHost,
        api_routes: {
          track: 'act/',
          engage: 'connect/',
        },
      })

      mixpanel.set_config({
        cross_subdomain_cookie: false,
        disable_persistence: true,
        ip: false,
        ignore_dnt: true,
      })

      mixpanel.register({
        businessUnit: 'goto',
        application: 'goto-gallery',
        gallery_id: context.galleryId,
        is_preview: context.preview,
        gallery_env: config.env,
      })

      initialized = true
    } catch (err) {
      getLogger().warn('[gallery-analytics] failed to initialize', err)
    }
  }

  const track = (eventName: string, properties: AnalyticsProperties): void => {
    if (!initialized) {
      return
    }

    try {
      mixpanel.track(eventName, getSafeProperties(eventName, properties))
    } catch (err) {
      getLogger().warn(`[gallery-analytics] failed to track ${eventName}`, err)
    }
  }

  return {
    initialize,
    track,
  }
}

const galleryAnalyticsService = createGalleryAnalyticsService()

export const initializeGalleryAnalytics = galleryAnalyticsService.initialize
export const trackGalleryEvent = galleryAnalyticsService.track
